import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import api from '../services/api.js';

const buildEditData = (record) => {
  const initialData = {};
  if (!record) return initialData;

  Object.keys(record).forEach((key) => {
    if (['hearingDate', 'caseDate', 'dob', 'fds', 'lds', 'solDate'].includes(key) && record[key]) {
      try {
        initialData[key] = new Date(record[key]).toISOString().split('T')[0];
      } catch (e) {
        initialData[key] = record[key];
      }
    } else if (Array.isArray(record[key])) {
      initialData[key] = record[key].map((item) => ({ ...item }));
    } else {
      initialData[key] = record[key];
    }
  });

  return initialData;
};

const deepCopy = (data) => JSON.parse(JSON.stringify(data));

const initialState = {
  record: null,
  recordLoading: true,
  recordError: null,
  isEditing: false,
  editData: {},
  initialEditData: {},
  isSaving: false,
  statusMessage: { type: '', text: '' },
  showUnsavedModal: false,
  modalDiscardAction: null,
  user: null,
  showCommentForm: false,
  comment: '',
  status: '',
  scheduledDate: '',
  scheduledTime: '',
  isSubmitting: false,
  commentError: '',
  timeSlots: [],
};

export const fetchHearingRecord = createAsyncThunk(
  'hearingRecordDetails/fetchRecord',
  async (recordId, { rejectWithValue }) => {
    try {
      const response = await api.get(`/records/${recordId}`);
      return response.data;
    } catch (err) {
      const message =
        err?.response?.data?.message || 'Failed to fetch hearing details. Please try again.';
      return rejectWithValue({ message });
    }
  }
);

export const fetchHearingUser = createAsyncThunk(
  'hearingRecordDetails/fetchUser',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get('/users/me');
      return response.data;
    } catch (err) {
      const message = err?.response?.data?.message || 'Failed to fetch user.';
      return rejectWithValue({ message });
    }
  }
);

export const saveHearingRecord = createAsyncThunk(
  'hearingRecordDetails/saveRecord',
  async ({ recordId, data }, { rejectWithValue }) => {
    try {
      const response = await api.put(`/records/${recordId}`, data);
      return response.data;
    } catch (err) {
      const message =
        err?.response?.data?.message || 'Failed to save record.';
      return rejectWithValue({ message });
    }
  }
);

export const addHearingComment = createAsyncThunk(
  'hearingRecordDetails/addComment',
  async ({ recordId, commentData }, { rejectWithValue }) => {
    try {
      const response = await api.post(`/records/${recordId}/comments`, commentData);
      return response.data;
    } catch (err) {
      const message =
        err?.response?.data?.message || 'Failed to add comment. Please try again.';
      return rejectWithValue({ message });
    }
  }
);

const hearingRecordDetailsSlice = createSlice({
  name: 'hearingRecordDetails',
  initialState,
  reducers: {
    setIsEditing(state, action) {
      state.isEditing = action.payload;
    },
    setEditData(state, action) {
      state.editData = action.payload;
    },
    updateEditField(state, action) {
      const { name, value } = action.payload;
      state.editData = { ...state.editData, [name]: value };
    },
    setIsSaving(state, action) {
      state.isSaving = action.payload;
    },
    setStatusMessage(state, action) {
      state.statusMessage = action.payload;
    },
    setShowUnsavedModal(state, action) {
      state.showUnsavedModal = action.payload;
    },
    setModalDiscardAction(state, action) {
      state.modalDiscardAction = action.payload;
    },
    resetEditData(state) {
      state.editData = deepCopy(state.initialEditData);
    },
    setShowCommentForm(state, action) {
      state.showCommentForm = action.payload;
    },
    setComment(state, action) {
      state.comment = action.payload;
    },
    setStatus(state, action) {
      state.status = action.payload;
    },
    setScheduledDate(state, action) {
      state.scheduledDate = action.payload;
    },
    setScheduledTime(state, action) {
      state.scheduledTime = action.payload;
    },
    setIsSubmitting(state, action) {
      state.isSubmitting = action.payload;
    },
    setCommentError(state, action) {
      state.commentError = action.payload;
    },
    setTimeSlots(state, action) {
      state.timeSlots = action.payload;
    },
    resetCommentForm(state) {
      state.showCommentForm = false;
      state.comment = '';
      state.status = '';
      state.scheduledDate = '';
      state.scheduledTime = '';
      state.isSubmitting = false;
      state.commentError = '';
      state.timeSlots = [];
    },
    updateMultiEntry(state, action) {
      const { fieldName, index, value } = action.payload;
      const newItems = state.editData[fieldName] ? [...state.editData[fieldName]] : [];
      if (newItems[index]) {
        newItems[index] = { ...newItems[index], value };
      } else {
        newItems[index] = { value };
      }
      state.editData = { ...state.editData, [fieldName]: newItems };
    },
    addMultiEntry(state, action) {
      const fieldName = action.payload;
      const newItems = state.editData[fieldName] ? [...state.editData[fieldName]] : [];
      newItems.push({ value: '' });
      state.editData = { ...state.editData, [fieldName]: newItems };
    },
    removeMultiEntry(state, action) {
      const { fieldName, index } = action.payload;
      const newItems = [...(state.editData[fieldName] || [])];
      newItems.splice(index, 1);
      state.editData = { ...state.editData, [fieldName]: newItems };
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchHearingRecord.pending, (state) => {
        state.recordLoading = true;
        state.recordError = null;
      })
      .addCase(fetchHearingRecord.fulfilled, (state, action) => {
        state.record = action.payload;
        const initialData = buildEditData(action.payload);
        state.editData = initialData;
        state.initialEditData = deepCopy(initialData);
        state.statusMessage = { type: '', text: '' };
        state.recordLoading = false;
      })
      .addCase(fetchHearingRecord.rejected, (state, action) => {
        state.recordError = action.payload?.message || 'Failed to fetch hearing details.';
        state.recordLoading = false;
      })
      .addCase(fetchHearingUser.fulfilled, (state, action) => {
        state.user = action.payload;
      })
      .addCase(saveHearingRecord.pending, (state) => {
        state.isSaving = true;
        state.statusMessage = { type: '', text: '' };
      })
      .addCase(saveHearingRecord.fulfilled, (state, action) => {
        state.record = action.payload;
        const initialData = buildEditData(action.payload);
        state.editData = initialData;
        state.initialEditData = deepCopy(initialData);
        state.isEditing = false;
        state.isSaving = false;
        state.statusMessage = { type: 'success', text: 'Record saved successfully!' };
      })
      .addCase(saveHearingRecord.rejected, (state, action) => {
        state.isSaving = false;
        state.statusMessage = { type: 'error', text: action.payload?.message || 'Failed to save record.' };
      })
      .addCase(addHearingComment.pending, (state) => {
        state.isSubmitting = true;
        state.commentError = '';
      })
      .addCase(addHearingComment.fulfilled, (state, action) => {
        state.record = action.payload;
        state.isSubmitting = false;
        state.showCommentForm = false;
        state.comment = '';
        state.status = '';
        state.scheduledDate = '';
        state.scheduledTime = '';
        state.commentError = '';
        state.timeSlots = [];
      })
      .addCase(addHearingComment.rejected, (state, action) => {
        state.isSubmitting = false;
        state.commentError =
          action.payload?.message || 'Failed to add comment. Please try again.';
      });
  },
});

export const {
  setIsEditing,
  setEditData,
  updateEditField,
  setIsSaving,
  setStatusMessage,
  setShowUnsavedModal,
  setModalDiscardAction,
  resetEditData,
  setShowCommentForm,
  setComment,
  setStatus,
  setScheduledDate,
  setScheduledTime,
  setIsSubmitting,
  setCommentError,
  setTimeSlots,
  resetCommentForm,
  updateMultiEntry,
  addMultiEntry,
  removeMultiEntry,
} = hearingRecordDetailsSlice.actions;

export const selectHearingRecord = (state) => state.hearingRecordDetails.record;
export const selectHearingRecordLoading = (state) => state.hearingRecordDetails.recordLoading;
export const selectHearingRecordError = (state) => state.hearingRecordDetails.recordError;
export const selectHearingIsEditing = (state) => state.hearingRecordDetails.isEditing;
export const selectHearingEditData = (state) => state.hearingRecordDetails.editData;
export const selectHearingInitialEditData = (state) => state.hearingRecordDetails.initialEditData;
export const selectHearingIsSaving = (state) => state.hearingRecordDetails.isSaving;
export const selectHearingStatusMessage = (state) => state.hearingRecordDetails.statusMessage;
export const selectHearingShowUnsavedModal = (state) => state.hearingRecordDetails.showUnsavedModal;
export const selectHearingModalDiscardAction = (state) =>
  state.hearingRecordDetails.modalDiscardAction;
export const selectHearingUser = (state) => state.hearingRecordDetails.user;

export const selectHearingShowCommentForm = (state) => state.hearingRecordDetails.showCommentForm;
export const selectHearingComment = (state) => state.hearingRecordDetails.comment;
export const selectHearingStatus = (state) => state.hearingRecordDetails.status;
export const selectHearingScheduledDate = (state) => state.hearingRecordDetails.scheduledDate;
export const selectHearingScheduledTime = (state) =>
  state.hearingRecordDetails.scheduledTime;
export const selectHearingIsSubmitting = (state) => state.hearingRecordDetails.isSubmitting;
export const selectHearingCommentError = (state) => state.hearingRecordDetails.commentError;
export const selectHearingTimeSlots = (state) => state.hearingRecordDetails.timeSlots;

export default hearingRecordDetailsSlice.reducer;
